// FirstPromoter affiliate tracking — single-file integration.
//
// What it does:
//   1. Loads the FirstPromoter JS at app boot when
//      REACT_APP_FIRSTPROMOTER_ACCOUNT is set. Their script captures
//      ?fpr=AFFILIATE_NAME from the URL, drops a cookie, and tracks
//      pageviews. Auto-no-op if the env var is missing (e.g. local
//      dev that doesn't have a FirstPromoter account configured).
//
//   2. Exposes readAffiliateCode() so SubscribePage can attach the
//      current visitor's affiliate code to the Stripe Checkout call
//      as referral_code. FirstPromoter's Stripe integration also
//      attributes via cookie + Stripe Customer email match, but
//      passing the code as Stripe Customer metadata gives the most
//      reliable server-side attribution.
//
// FirstPromoter docs reference:
//   https://help.firstpromoter.com/article/162-install-tracking-script
//
// Setup on FirstPromoter dashboard (separate from code):
//   - Connect Stripe via OAuth (read-only on subscriptions)
//   - Set commission tiers, clawback window
//   - Get your account name (subdomain on FirstPromoter, e.g.
//     "agencyhub" if your dashboard is agencyhub.firstpromoter.com)
//   - Add REACT_APP_FIRSTPROMOTER_ACCOUNT=<that-name> to Vercel

const ACCOUNT = process.env.REACT_APP_FIRSTPROMOTER_ACCOUNT || '';
const COOKIE_KEY = '_fprom_ref';      // FirstPromoter's default cookie name
const LS_KEY     = 'sfa_fpr';          // our local mirror (survives cookie purge)
const LS_AT_KEY  = 'sfa_fpr_at';
const WINDOW_DAYS = 60;                // attribution window

let injected = false;

// Inject the FirstPromoter tracking script. Uses their recommended
// init+click stub so attribution fires reliably across SPA route
// changes (vs the legacy data-fp-aid pattern which sometimes misses
// late clicks). Idempotent — multiple calls are no-op.
export function initFirstPromoter() {
  if (injected || !ACCOUNT) return;
  if (typeof window === 'undefined') return;
  injected = true;

  // Inline stub — queues init + click events until fpr.js loads,
  // then they fire in order. This matches the exact snippet
  // FirstPromoter generates for new accounts.
  (function (w) {
    w.fpr = w.fpr || function () {
      w.fpr.q = w.fpr.q || [];
      w.fpr.q[arguments[0] === 'set' ? 'unshift' : 'push'](arguments);
    };
    w.fpr('init', { cid: ACCOUNT });
    w.fpr('click');
  })(window);

  // Async load the actual tracking script.
  const s = document.createElement('script');
  s.src   = 'https://cdn.firstpromoter.com/fpr.js';
  s.async = true;
  document.head.appendChild(s);
}

// Capture ?fpr=X from the URL on first paint + mirror to localStorage
// with a 60-day window. Returns the code (string) or '' if none.
// Safe to call on every page load.
export function captureFprFromUrl() {
  if (typeof window === 'undefined') return '';
  try {
    const q = new URLSearchParams(window.location.search);
    const incoming = String(q.get('fpr') || '').trim();
    if (incoming) {
      localStorage.setItem(LS_KEY, incoming);
      localStorage.setItem(LS_AT_KEY, String(Date.now()));
      return incoming;
    }
  } catch { /* ignore */ }
  return '';
}

// Read the currently-attributed affiliate code. Tries the
// FirstPromoter cookie first (their JS sets it), then our
// localStorage mirror, then expires anything past WINDOW_DAYS.
export function readAffiliateCode() {
  if (typeof window === 'undefined') return '';
  // 1. FirstPromoter cookie (canonical)
  try {
    const ck = (document.cookie || '').split(';').map(s => s.trim()).find(s => s.startsWith(`${COOKIE_KEY}=`));
    if (ck) {
      const v = decodeURIComponent(ck.slice(COOKIE_KEY.length + 1));
      if (v) return v;
    }
  } catch { /* ignore */ }
  // 2. Local mirror, checking the window
  try {
    const v = localStorage.getItem(LS_KEY);
    const at = Number(localStorage.getItem(LS_AT_KEY)) || 0;
    if (v && (Date.now() - at) < WINDOW_DAYS * 86_400_000) return v;
    if (v && (Date.now() - at) >= WINDOW_DAYS * 86_400_000) {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_AT_KEY);
    }
  } catch { /* ignore */ }
  return '';
}

// Optional: signal a custom conversion event to FirstPromoter from
// the success page. The Stripe integration (server-side) usually
// catches the conversion automatically, but if you want to also fire
// a client-side beacon (for instant dashboard updates), call this on
// /?subscribe=success.
export function trackConversion({ email, uid } = {}) {
  if (typeof window === 'undefined' || !window.$FPROM) return;
  try {
    window.$FPROM.trackSignup(
      { email: email || null, uid: uid || null },
      () => {/* fp callback */}
    );
  } catch { /* ignore */ }
}
